import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppUser from "@toothfairy/shared-ui/AppUser";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isConnectedToCloud, userType } from "../../App";
import useWorkspaces from "./useWorkspaces";
import { isMobilePeeringVisible } from "../Utils/peeringPreview";

export const useSettingsMenuItems = () => {
  const { t } = useTranslation();
  const { getActiveWorkspace, isUserAIEngineer, getPlanConfig } =
    useWorkspaces();
  const { state } = AppStateManager.useAppStateManager();

  return useMemo(() => {
    if (!userType()) return [];
    let options = [
      {
        isNavigation: true,
        icon: AppAssets.icons.collection,
        uid: "TOPIC",
        label: t("settings.topic"),
        subOptions: [],
        supportVisible: true,
      },
    ];
    if (isUserAIEngineer() && isConnectedToCloud()) {
      options = [
        {
          isNavigation: true,
          icon: AppAssets.icons.taskAgent,
          uid: "ROLES_SETTINGS",
          label: t("settings.agents"),
          subOptions: [],
          supportVisible: true,
          fullWidth: true,
          aiEngineerOnly: true,
        },
        {
          isNavigation: true,
          icon: AppAssets.icons.writer,
          uid: "GENERATOR_SETTINGS",
          label: t("settings.prompts"),
          subOptions: [],
          supportVisible: true,
          fullWidth: true,
          aiEngineerOnly: true,
        },
        ...(getActiveWorkspace()?.subscriptionType != "base"
          ? [
              {
                isNavigation: true,
                icon: AppAssets.icons.days,
                uid: "JOBS",
                label: t("settings.jobs"),
                subOptions: [],
                supportVisible: true,
                fullWidth: true,
                aiEngineerOnly: true,
              },
            ]
          : []),
        ...(getActiveWorkspace()?.subscriptionType != "base"
          ? [
              {
                isNavigation: true,
                icon: AppAssets.icons.bolt,
                uid: "TRIGGERS",
                label: t("settings.triggers"),
                subOptions: [],
                supportVisible: true,
                fullWidth: true,
                aiEngineerOnly: true,
              },
            ]
          : []),

        {
          isNavigation: true,
          icon: AppAssets.icons.functions,
          uid: "FUNCTIONS",
          label: t("settings.tools"),
          subOptions: [],
          supportVisible: true,
          fullWidth: true,
          aiEngineerOnly: true,
        },
        {
          isNavigation: true,
          icon: AppAssets.icons.data,
          uid: "DB_CONNECTIONS",
          label: t("settings.connections"),
          subOptions: [],
          supportVisible: true,
          fullWidth: true,
          aiEngineerOnly: true,
        },
        {
          isNavigation: true,
          icon: AppAssets.icons.credentials,
          uid: "AUTHORISATIONS",
          label: t("settings.authorisations"),
          subOptions: [],
          supportVisible: true,
          fullWidth: true,
          aiEngineerOnly: true,
        },
        {
          isNavigation: true,
          icon: AppAssets.icons.hosting,
          uid: "HOSTINGS",
          label: t("settings.providers"),
          subOptions: [],
          supportVisible: true,
          fullWidth: true,
          aiEngineerOnly: true,
        },
        {
          isNavigation: true,
          icon: AppAssets.icons.channels,
          uid: "CHANNELS",
          label: t("channels.title"),
          subOptions: [],
          supportVisible: true,
          fullWidth: true,
          aiEngineerOnly: true,
        },
        {
          isNavigation: true,
          icon: AppAssets.icons.terminal,
          uid: "EXEC_ENVS",
          label: t("settings.hooks"),
          subOptions: [],
          supportVisible: true,
          fullWidth: true,
          aiEngineerOnly: true,
        },

        {
          isNavigation: true,
          icon: AppAssets.icons.notification,
          uid: "ALERTS",
          label: t("settings.alerts", "Alerts"),
          subOptions: [],
          supportVisible: true,
          fullWidth: true,
          aiEngineerOnly: true,
        },

        {
          isNavigation: true,
          icon: AppAssets.icons.web,
          uid: "SITES",
          label: t("settings.domains"),
          subOptions: [],
          supportVisible: true,
          fullWidth: true,
          aiEngineerOnly: true,
        },
        {
          isNavigation: true,
          icon: AppAssets.icons.benchmark,
          uid: "BENCHMARKS",
          label: t("settings.benchmarks"),
          subOptions: [],
          supportVisible: true,
          fullWidth: true,
          aiEngineerOnly: true,
        },
        {
          isNavigation: true,
          icon: AppAssets.icons.language,
          uid: "DICTIONARY",
          label: t("settings.dictionaries"),
          subOptions: [],
          supportVisible: true,
          fullWidth: true,
          aiEngineerOnly: true,
        },
        {
          isNavigation: true,
          icon: AppAssets.icons.collection,
          uid: "TOPIC",
          label: t("settings.topics"),
          subOptions: [],
          supportVisible: true,
        },
      ];
      options.push({
        isNavigation: true,
        icon: AppAssets.icons.ner,
        uid: "NER",
        label: t("settings.ners"),
        subOptions: [],
        supportVisible: true,
        aiEngineerOnly: true,
      });
    } else if (isUserAIEngineer() && !isConnectedToCloud()) {
      options = [
        {
          isNavigation: true,
          icon: AppAssets.icons.taskAgent,
          uid: "ROLES_SETTINGS",
          label: t("settings.agents"),
          subOptions: [],
          supportVisible: true,
          fullWidth: true,
          aiEngineerOnly: true,
        },
      ];
    }
    if (
      getPlanConfig()?.isTrainingNLPEnabled ||
      getPlanConfig()?.isTrainingGNEnabled ||
      getPlanConfig()?.isTrainingQAEnabled
    ) {
      options.push({
        isNavigation: true,
        icon: AppAssets.icons.trainAI,
        uid: "TRAINING",
        label: t("training.title"),
        subOptions: [],
        supportVisible: true,
        aiEngineerOnly: true,
      });
    }
    if (getActiveWorkspace()?.id) return options;
    return undefined;
  }, [
    JSON.stringify(getActiveWorkspace()),
    JSON.stringify(state.aiEntitiesConfig),
    userType(),
    isConnectedToCloud(),
    t,
  ]);
};

export const useAdminMenuItems = () => {
  const { t } = useTranslation();
  const { getActiveWorkspace, isUserAdmin, isToothFairyStaff } =
    useWorkspaces();
  const supportMenuVisible = isUserAdmin() || isToothFairyStaff();

  return useMemo(() => {
    const subscriptionType = getActiveWorkspace()?.subscriptionType;
    const isBusinessOrEnterprise =
      subscriptionType === "business" || subscriptionType === "enterprise";
    const _base_options = [
      {
        isNavigation: true,
        icon: AppAssets.icons.cogWheel,
        isBackHidden: false,
        uid: "WORKSPACE_SETTINGS",
        label: t("admin.menu.generalSettings"),
        subOptions: [],
        supportVisible: true,
      },
      ...(isBusinessOrEnterprise
        ? [
            {
              isNavigation: true,
              icon: AppAssets.icons.usersSettings,
              isBackHidden: false,
              uid: "USERS",
              label: t("admin.menu.userManagement"),
              subOptions: [],
              supportVisible: false,
            },
          ]
        : []),
      {
        isNavigation: true,
        icon: AppAssets.icons.feedback,
        isBackHidden: false,
        uid: "HUMAN_FEEDBACK",
        label: t("admin.menu.humanFeedback"),
        subOptions: [],
        supportVisible: false,
      },
      {
        isNavigation: true,
        icon: AppAssets.icons.tfcode,
        isBackHidden: false,
        uid: "API_INTEGRATION",
        label: isBusinessOrEnterprise
          ? t("admin.menu.tfCodeApiIntegration")
          : t("admin.menu.tfCodeIntegration"),
        subOptions: [],
        supportVisible: false,
      },
      ...(isBusinessOrEnterprise
        ? [
            {
              isNavigation: true,
              icon: AppAssets.icons.webhook,
              isBackHidden: false,
              uid: "WEBHOOK",
              label: t("admin.menu.webhook"),
              subOptions: [],
              supportVisible: false,
            },
          ]
        : []),
      {
        isNavigation: true,
        icon: AppAssets.icons.charting,
        uid: "CHARTING",
        label: t("admin.menu.chartingSettings"),
        subOptions: [],
        supportVisible: false,
      },
      {
        isNavigation: true,
        icon: AppAssets.icons.embeddings,
        uid: "EMBEDDING",
        label: t("admin.menu.embeddingsSettings"),
        subOptions: [],
        supportVisible: false,
      },
      {
        isNavigation: true,
        icon: AppAssets.icons.notification,
        uid: "NOTIFICATIONS",
        label: t("admin.menu.notifications"),
        subOptions: [],
        supportVisible: false,
      },
    ];
    if (getActiveWorkspace()?.subscriptionType === "enterprise") {
      _base_options.push(
        {
          isNavigation: true,
          icon: AppAssets.icons.shield,
          isBackHidden: false,
          uid: "SSO",
          label: t("admin.menu.singleSignOn"),
          subOptions: [],
          supportVisible: false,
        }
        // {
        //   isNavigation: true,
        //   icon: AppAssets.icons.notes,
        //   uid: "LOGS",
        //   label: t("admin.menu.logs"),
        //   subOptions: [],
        //   supportVisible: false,
        // }
      );
    }
    if (supportMenuVisible) {
      _base_options.push({
        isNavigation: true,
        icon: AppAssets.icons.support,
        isBackHidden: false,
        uid: "SUPPORT",
        label: t("admin.menu.support"),
        subOptions: [],
        supportVisible: false,
      });
    }
    return _base_options;
  }, [
    getActiveWorkspace()?.id,
    getActiveWorkspace()?.subscriptionType,
    supportMenuVisible,
    t,
  ]);
};

export const useProfileMenuItems = () => {
  const { t } = useTranslation();
  const { user } = AppUser.useUser();

  return useMemo(
    () => [
      {
        isNavigation: true,
        icon: AppAssets.icons.userInfo,
        isBackHidden: false,
        uid: "PROFILE_SETTINGS",
        label: t("profile.tabs.profileSettings"),
        subOptions: [],
      },
      ...(isMobilePeeringVisible(user)
        ? [
            {
              isNavigation: true,
              icon: AppAssets.icons.desktop,
              uid: "MOBILE_PEERING",
              label: t("profile.tabs.mobilePeering", "Mobile peering"),
              subOptions: [],
            },
          ]
        : []),
      {
        isNavigation: true,
        icon: AppAssets.icons.settings,
        uid: "NLP",
        label: t("profile.tabs.userPreferences"),
        subOptions: [],
      },
      {
        isNavigation: true,
        icon: AppAssets.icons.billing,
        uid: "BILLING",
        label: t("profile.tabs.billing"),
        subOptions: [],
      },
    ],
    [t, user]
  );
};

export default useSettingsMenuItems;
